import { useHealthblocksSWR } from '@healthblocks-io/core'
import { Fragment, useEffect, useState } from 'react'

import { useAdminMessage } from '@/modules/healthblocks/hooks/useAdminMessage'
import { AdminMessageCard } from '@/modules/shared/components/admin-message-card/AdminMessageCard'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { SynopsNoConnectionPage } from './SynopsNoConnection.page'

import type { ReactNode } from 'react'

type Props = {
  children: ReactNode | ReactNode[]
}

const SynopsConnected = ({ children }: Props) => {
  const [reconnecting, setReconnecting] = useState(true)
  const { adminMessage } = useAdminMessage({ refreshInterval: 1000 * 60 * 1 })
  const swr = useHealthblocksSWR<'true'>('/integration/integration-synops/endpoint/synops/health', {
    refreshInterval: 60 * 1000,
    errorRetryInterval: 10 * 1000,
  })

  // Don't instantly switch to no connection mode if connection is lost. Give it a 5 second window to reconnect
  useEffect(() => {
    if (swr.error) {
      const timeout = setTimeout(() => {
        setReconnecting(false)
      }, 5000)
      return () => {
        clearTimeout(timeout)
      }
    }
    // Reset to default state
    if (!swr.error && swr.data && !reconnecting) setReconnecting(true)
  }, [!!swr.error, swr.data])

  // Happy path: Synops responded without error
  if (swr.data && (!swr.error || reconnecting)) {
    return <Fragment key='online'>{children}</Fragment>
  }

  // Hmm, Synops did not respond yet
  if (swr.isLoading) {
    return <AnimatedLoader cancellable />
  }

  // Informational admin message shown as banner above the error screen
  if (adminMessage && !adminMessage.blocking) {
    return (
      <>
        <AdminMessageCard message={adminMessage.message} />
        <SynopsNoConnectionPage />
      </>
    )
  }

  return <SynopsNoConnectionPage />
}

export { SynopsConnected }
